import React, { useEffect, useRef, useState } from 'react';
import { 
  Landmark, 
  Receipt, 
  Megaphone, 
  Star, 
  GraduationCap, 
  Gift,
  TrendingUp,
  Briefcase,
  LineChart
} from 'lucide-react';
import Button from '../SecondReceptionistComponents/button/Button';
import styles from './SeventhScreen.module.css';

// Import expert images
import zahiImage from '../recImages/zahi.jpeg';
import nadavImage from '../recImages/nadav.jpeg';
import michaImage from '../recImages/micha.jpeg';
import talImage from '../recImages/tal.jpeg'; // הוסף את התמונה של טל

const ExpertsTimeline = () => {
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const cardsRef = useRef([]);
  const dotsRef = useRef([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  const experts = [
    {
      id: 1,
      name: 'זכי ויינשטיין',
      profession: 'מנסיף סניף בנק דיסקונט',
      role: 'מנהל סניף בנק עסקי',
      description: 'קודם כל כלכלן, מנהל בכיר, MBA, יועץ השקעות ומתכנן פיננסי CFP. מנהל בכיר, סניף נתניה בנק דיסקונט.',
      specialty: 'ניהול כספים נכון',
      Icon: Landmark,
      RoleIcon: Star,
      SpecialtyIcon: GraduationCap,
      image: zahiImage
    },
    {
      id: 2,
      name: 'נדב קאהן',
      profession: 'יועץ מס',
      role: 'מומחה למיסוי וניהול כספים',
      description: 'בעל ניסיון רב בליווי עסקים קטנים ובינוניים. עוזר לך להבין את המסגרת החוקית והפיננסית של העסק שלך.',
      specialty: 'המסגרת החוקית של העסק',
      Icon: Receipt,
      RoleIcon: Briefcase,
      SpecialtyIcon: GraduationCap,
      image: nadavImage
    },
    {
      id: 3,
      name: 'מיכאל דונל',
      profession: 'מומחה שיווק',
      role: 'שיווק דיגיטלי ולקוחות פרימיום',
      description: 'מומחה לשיווק דיגיטלי עם ניסיון של שנים בגיוס לקוחות איכותיים. מלמד אותך את הסודות מהשטח.',
      specialty: 'איך משיגים לקוחות פרימיום',
      Icon: Megaphone,
      RoleIcon: Star,
      SpecialtyIcon: GraduationCap,
      image: michaImage
    },
    {
      id: 4,
      name: 'טל קלמפנר',
      profession: 'מתכנן פיננסי',
      role: 'מתכנן פיננסי בכיר',
      description: 'ניסיון של 20 שנה בשוק ההון. מגיע מעולם של השקעות כספים פנויים, פנסיות, השתלמויות וביטוחים. מתמחה בבניית אסטרטגיות השקעה עם דגש על מיסוי והעברה בין דורית.',
      specialty: 'אסטרטגיות השקעה והעברה בין דורית',
      Icon: TrendingUp,
      RoleIcon: LineChart,
      SpecialtyIcon: GraduationCap,
      image: talImage // החלף לתמונה של טל כשתהיה זמינה
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || !progressRef.current) return;
      
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const scrollProgress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
      
      if (progressRef.current) {
        progressRef.current.style.height = `${scrollProgress * 100}%`;
      }

      cardsRef.current.forEach((card, index) => {
        if (card) {
          const cardRect = card.getBoundingClientRect();
          const cardCenter = cardRect.top + cardRect.height / 2;
          const viewportCenter = window.innerHeight / 2;
          
          const threshold = window.innerWidth <= 768 ? 200 : 250;
          if (Math.abs(cardCenter - viewportCenter) < threshold) {
            setActiveIndex(index);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ScrollReveal effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: '0px 0px -80px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, idx) => {
        if (entry.isIntersecting) {
          // Add staggered delay based on card index
          const cardIndex = cardsRef.current.indexOf(entry.target);
          const delay = cardIndex * 100;
          
          setTimeout(() => {
            entry.target.classList.add(styles.cardRevealed);
          }, delay);
        }
      });
    }, observerOptions);

    cardsRef.current.forEach((card) => {
      if (card) {
        card.classList.add(styles.cardHidden);
        observer.observe(card);
      }
    });

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) {
          observer.unobserve(card);
        }
      });
    };
  }, []);

  return <>
    <section ref={containerRef} className={styles.container}>
      {/* Background gradient */}
      <div className={styles.backgroundGradient} />
      
      {/* Animated particles */}
      <div className={styles.particlesContainer}>
        {[...Array(20)].map((_, i) => (
          <div key={i} className={styles.particle} style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${Math.random() * 8 + 3}px`,
            height: `${Math.random() * 8 + 3}px`,
            animationDelay: `${i * 0.3}s`,
          }} />
        ))}
      </div>

      <div className={styles.contentWrapper}>
        {/* Header */}
        <div className={styles.headerSection}>
         
   
          <p className={styles.subtitle}>
            הבאתי את הטובים ביותר שילמדו אותך את הסודות בצורה הכי פרקטית שיש
          </p>
                 <h2 className={styles.titleMain}>
            תכירי את המומחים שלנו
          </h2>
        </div>

        <div className={styles.timelineContainer}>
          {/* Timeline spine - white */}
          <div className={styles.timelineSpine}>
            <div ref={progressRef} className={styles.timelineProgress} />
          </div>

          {/* Expert cards */}
          <div className={styles.cardsContainer}>
            {experts.map((expert, index) => {
              const ExpertIcon = expert.Icon;
              const RoleIcon = expert.RoleIcon;
              const SpecialtyIcon = expert.SpecialtyIcon;
              
              return (
                <div 
                  key={expert.id}
                  className={`${styles.timelineItem} ${index % 2 === 0 ? styles.itemRight : styles.itemLeft}`}
                >
                  {/* Timeline dot - white */}
                  <div 
                    ref={(el) => dotsRef.current[index] = el}
                    className={`${styles.timelineDot} ${activeIndex === index ? styles.timelineDotActive : ''}`}
                  >
                    <div className={styles.dotInner}>
                      <div className={styles.dotIcon}>
                        <ExpertIcon size={28} color="#805E3C" strokeWidth={1.5} />
                      </div>
                    </div>
                    {activeIndex === index && (
                      <div className={styles.dotPulse} />
                    )}
                  </div>

                  {/* Connecting line */}
                  <div className={`${styles.connectingLine} ${index % 2 === 0 ? styles.connectingLineRight : styles.connectingLineLeft}`} />

                  {/* Expert card - unified background */}
                  <div 
                    ref={(el) => cardsRef.current[index] = el}
                    className={`${styles.expertCard} ${activeIndex === index ? styles.expertCardActive : ''}`}
                  >
                    {/* Card content */}
                    <div className={styles.cardLayout}>
                      {/* Header row - Image + Name + Profession */}
                      <div className={styles.cardHeader}>
                        <div className={styles.avatarWrapper}>
                          <div className={styles.avatarGlow} />
                          <div className={styles.imagePlaceholder}>
                            <img 
                              src={expert.image} 
                              alt={expert.name}
                              className={styles.expertImage}
                            />
                          </div>
                          <div className={styles.imageRing} />
                        </div>
                        
                        <div className={styles.headerInfo}>
                          <h3 className={styles.expertName}>{expert.name}</h3>
                          <span className={styles.expertProfession}>
                            {expert.profession}
                          </span>
                        </div>
                      </div>

                      {/* Content section */}
                      <div className={styles.cardContent}>
                        <div className={styles.roleContainer}>
                          <div className={styles.roleIcon}>
                            <RoleIcon size={20} color="#805E3C" strokeWidth={1.5} />
                          </div>
                          <p className={styles.expertRole}>{expert.role}</p>
                        </div>

                        <p className={styles.expertDescription}>
                          {expert.description}
                        </p>
                        
                        <div className={styles.specialtyTag}>
                          <div className={styles.specialtyIcon}>
                            <SpecialtyIcon size={20} color="#805E3C" strokeWidth={1.5} />
                          </div>
                          <span className={styles.specialtyText}>{expert.specialty}</span>
                        </div>
                      </div>
                    </div>

                    {/* Decorative corner elements */}
                    <div className={styles.cornerTopLeft} />
                    <div className={styles.cornerBottomRight} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* CTA Section */}
      </div>
          <Button text="אני רוצה לשמוע עוד על הקורס" />
    </section>
  </>
};

export default ExpertsTimeline;